import { toast, useConfirmation } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCurrentUser } from 'modules/utils';
import { useCallback, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';
import { hasChangesState } from 'shared/states';

import {
    deleteReport,
    discardChanges,
    downloadAttachmentArchive,
    downloadAuditReports,
    executeReport,
    exportAsCsv,
    renameActiveReport,
    saveReport,
    saveReportAsNew,
    showPrintPreview,
    showXeroAccrualReportPopup,
} from '../../actions';
import { getReportConfigTransfer } from '../../selectors/reportConfigSelectors';
import { ReportConfigExpanded } from '../../types/ReportConfig';
import { reportSchedulerOpenState } from '../ReportSchedulerPopup/ReportSchedulerPopup.states';
import { useXeroAccrualManualJournalPopupIsAvailable } from '../XeroAccrualManualJournalPopup/XeroAccrualManualJournalPopup.hooks';
import { xeroAccrualManualJournalState } from '../XeroAccrualManualJournalPopup/XeroAccrualManualJournalPopup.states';
import { messages } from './ReportToolbar.messages';

export const useGetActiveIntegration = (company: selectors.types.ExpandedCompany) => {
    const integrationId = company.integrationId;

    return useSelector((state) =>
        integrationId ? selectors.integration.findIntegrationById(state.entities.integrations, integrationId) : null
    );
};

export const useReportToolbar = (reportConfig: ReportConfigExpanded) => {
    const dispatch = useDispatch();
    const { confirmation } = useConfirmation({
        confirmMessage: messages.removeReportConfirmationText,
        confirmButtonMessage: messages.removeReportConfirmationYesText,
        cancelButtonMessage: messages.removeReportConfirmationNoText,
    });

    const handleRemoveReport = useCallback(() => {
        confirmation()
            .then(() => dispatch(deleteReport()))
            .catch(() => {});
    }, [confirmation, dispatch]);

    const handlePrintReport = useCallback(() => {
        dispatch(showPrintPreview());
    }, [dispatch]);

    const handleExportAsCsv = useCallback(() => {
        dispatch(exportAsCsv());
    }, [dispatch]);

    const handleRefreshReport = useCallback(() => {
        dispatch(executeReport());
    }, [dispatch]);

    const handleSaveReportName = useCallback(
        (reportName: string) => {
            if (reportName !== reportConfig.name) {
                dispatch(renameActiveReport(reportName));
            }
        },
        [dispatch, reportConfig.name]
    );

    return {
        handleExportAsCsv,
        handleRefreshReport,
        handleRemoveReport,
        handlePrintReport,
        handleSaveReportName,
    };
};

export const useSaveAsNewReport = (reportConfig: ReportConfigExpanded) => {
    const dispatch = useDispatch();

    const handleSaveAsNewReport = useCallback(() => {
        dispatch(saveReportAsNew());
    }, [dispatch]);

    return {
        isAvailable: !reportConfig.isNew,
        isDisabled: !reportConfig.isValid,
        handleSaveAsNewReport,
    };
};

export const useSaveChanges = (reportConfig: ReportConfigExpanded) => {
    const dispatch = useDispatch();

    const saveChanges = useCallback(() => {
        if (!reportConfig.isValid) {
            toast.error(messages.invalidReportSaveError);

            return;
        }

        dispatch(saveReport());
    }, [dispatch, reportConfig.isValid]);

    return {
        isDisabled: !reportConfig.isValid,
        saveChanges,
    };
};

export const useDiscardChanges = () => {
    const dispatch = useDispatch();
    const { confirmation } = useConfirmation({
        confirmMessage: messages.discardChangesConfirmationText,
        confirmButtonMessage: messages.discardChangesConfirmationYesText,
        cancelButtonMessage: messages.discardChangesConfirmationNoText,
    });

    const hasChanges = useAtomValue(hasChangesState);

    const handleDiscardChanges = useCallback(() => {
        confirmation()
            .then(() => dispatch(discardChanges()))
            .catch(() => {});
    }, [confirmation, dispatch]);

    return {
        isDisabled: !hasChanges,
        handleDiscardChanges,
    };
};

export const useScheduleReportPopup = () => {
    const [isVisible, setIsVisible] = useAtom(reportSchedulerOpenState);

    const handleOpen = useCallback(() => {
        setIsVisible(true);
    }, [setIsVisible]);

    return {
        isVisible,
        handleOpen,
    };
};

export const useAccrualManualJournalPopup = (
    reportConfig: ReportConfigExpanded,
    company: selectors.types.ExpandedCompany,
    handleSaveChanges: () => void
) => {
    const hasChanges = useAtomValue(hasChangesState);
    const isAvailable = useXeroAccrualManualJournalPopupIsAvailable(company, reportConfig);
    const setIsVisible = useSetAtom(xeroAccrualManualJournalState);

    const currentUser = useCurrentUser();

    const companyTemplates = useSelector((state) => selectors.template.getTemplatesByCompanyId(state, company.id));

    const xeroManualJournalWorkflow = useMemo(
        () =>
            companyTemplates.find((template) => template.integrationCode === domain.IntegrationCode.XeroManualJournal),
        [companyTemplates]
    );

    const { confirmation } = useConfirmation({
        confirmMessage: messages.forceSaveDescription,
        confirmButtonMessage: messages.forceSaveButtonTextYes,
        cancelButtonMessage: messages.forceSaveButtonTextNo,
    });

    const handleOpen = useCallback(() => {
        const isRequesterInManualJournal =
            xeroManualJournalWorkflow?.enabled &&
            xeroManualJournalWorkflow?.submitterMatrix.some((submitter) => submitter.lineId === currentUser.id);

        if (!isRequesterInManualJournal) {
            toast.error(messages.activateManualJournal);

            return;
        }

        if (hasChanges) {
            confirmation()
                .then(() => handleSaveChanges())
                .then(() => setIsVisible(true))
                .catch(() => {});
        } else {
            setIsVisible(true);
        }
    }, [confirmation, handleSaveChanges, hasChanges, setIsVisible, xeroManualJournalWorkflow, currentUser.id]);

    return {
        isAvailable,
        handleOpen,
    };
};

export const useXeroAccrualReportPopup = (reportConfig: ReportConfigExpanded) => {
    const dispatch = useDispatch();
    const company = useCompanyBasedOnUrl();
    const integration = useGetActiveIntegration(company);
    const reportConfigTransfer = useSelector((state) => getReportConfigTransfer(state, reportConfig));

    const isIntegrationEnabled = integration?.status === domain.IntegrationStatus.Connected;
    const isXeroIntegration = integration?.integrationType === domain.IntegrationType.Xero;
    const isXeroContact = reportConfigTransfer.settings.templateId?.filteringValue.some(
        (filteringValue: any) => filteringValue.integrationCode === domain.IntegrationCode.XeroContact
    );

    const handleOpen = useCallback(() => {
        dispatch(showXeroAccrualReportPopup());
    }, [dispatch]);

    const isAvailable = isXeroIntegration && reportConfig.reportType !== domain.ReportType.LineItem && !isXeroContact;
    const isDisabled = !isIntegrationEnabled;

    return {
        isAvailable,
        isDisabled,
        handleOpen,
    };
};

export const useDownloadAuditReports = (reportConfig: ReportConfigExpanded) => {
    const dispatch = useDispatch();

    const handleDownload = useCallback(() => {
        dispatch(downloadAuditReports());
    }, [dispatch]);

    return {
        isAvailable: reportConfig.reportType !== domain.ReportType.LineItem,
        handleDownload,
    };
};

export const useDownloadAttachmentsArchive = (reportConfig: ReportConfigExpanded) => {
    const dispatch = useDispatch();

    const handleDownload = useCallback(() => {
        dispatch(downloadAttachmentArchive());
    }, [dispatch]);

    return {
        isAvailable: reportConfig.reportType !== domain.ReportType.LineItem,
        handleDownload,
    };
};

export const useReportToolbarDivider = (
    reportConfig: ReportConfigExpanded,
    company: selectors.types.ExpandedCompany
) => {
    const integration = useGetActiveIntegration(company);

    const isXeroIntegration = integration?.integrationType === domain.IntegrationType.Xero;

    const reportConfigTransfer = useSelector((state) => getReportConfigTransfer(state, reportConfig));
    const isXeroContact = useMemo(
        () =>
            reportConfigTransfer.settings.templateId?.filteringValue.some(
                (filteringValue: any) => filteringValue.integrationCode === domain.IntegrationCode.XeroContact
            ),
        [reportConfigTransfer.settings.templateId?.filteringValue]
    );

    return reportConfig.reportType !== domain.ReportType.LineItem && isXeroIntegration && !isXeroContact;
};
